import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  useTheme,
  useMediaQuery,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from "@mui/material";
import { FormattedMessage, useIntl } from "react-intl";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { AppDispatch, RootState } from "../../redux/store";
import SpecialClassAccessDisplay from "./SpecialClassAccessDisplay";
import {
  deleteAttendee,
  updateAttendee,
} from "../../redux/enrollment/enrollmentOperations";
import { Attendee } from "../../redux/types/types";
import { getProfile } from "../../redux/auth/authOperations";
import NewAttendeeForm from "./NewAttendeeForm/NewAttendeeForm";
import { PaymentDialog } from "../Payment";
import { LatinTextField } from "../../helpers/latinTextField";
import Notiflix from "notiflix";
import { isValidPhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import EmailTextField from "../../helpers/EmailTextField";
import { isValidPostalCode } from "postal-code-validator";
import PhoneField from "../../helpers/PhoneField";
import { fetchPaymentHistoryApi } from "../../utils/api/paymentHistory";
import {
  getAttendeeEnrolledClassIdsApi,
  getAttendeeImportantInfoApi,
} from "../../utils/api/attendees";
import { shouldShowClassCounters } from "../../utils/specialClassAccess";
import AttendeeImportantInfoButton from "./AttendeeImportantInfoButton";

export default function Attendees() {
  const dispatch = useDispatch<AppDispatch>();
  const intl = useIntl();
  const attendees = useSelector(
    (state: RootState) => state.auth.user.attendees,
  );
  const isAdmin = useSelector((state: RootState) => state.auth.isAdmin);
  const userClasses = useSelector(
    (state: RootState) => state.auth.user.classes || [],
  );
  const adminClasses = useSelector(
    (state: RootState) => state.admin.classes || [],
  );
  const classes = isAdmin ? adminClasses : userClasses;
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editedAttendee, setEditedAttendee] = useState<Partial<Attendee>>({});
  const [isNewAttendeeFormOpen, setIsNewAttendeeFormOpen] = useState(false);
  const [paymentHistoryOpen, setPaymentHistoryOpen] = useState(false);
  const [paymentHistory, setPaymentHistory] = useState<any>(null);
  const [selectedAttendee, setSelectedAttendee] = useState<Attendee | null>(
    null,
  );
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [paymentSelectedAttendee, setPaymentSelectedAttendee] =
    useState<Attendee | null>(null);
  const [enrolledClassIds, setEnrolledClassIds] = useState<number[]>([]);
  const [attendeeEnrolledClasses, setAttendeeEnrolledClasses] = useState<
    Map<number, number[]>
  >(new Map());

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    dispatch(getProfile());
  }, [dispatch]);

  // Fetch enrolled class IDs for all attendees
  useEffect(() => {
    const fetchEnrolledClasses = async () => {
      if (!attendees || attendees.length === 0) return;

      const enrolledMap = new Map<number, number[]>();
      for (const attendee of attendees) {
        try {
          const response = await getAttendeeEnrolledClassIdsApi(attendee.id);
          enrolledMap.set(attendee.id, response.data.enrolledClassIds || []);
        } catch (error) {
          console.error(
            `Failed to fetch enrolled classes for attendee ${attendee.id}:`,
            error,
          );
          enrolledMap.set(attendee.id, []);
        }
      }
      setAttendeeEnrolledClasses(enrolledMap);
    };

    fetchEnrolledClasses();
  }, [attendees]);

  const validationSchema = Yup.object().shape({
    firstName: Yup.string().required(
      intl.formatMessage({ id: "signUp.firstNameRequired" }),
    ),
    lastName: Yup.string().required(
      intl.formatMessage({ id: "signUp.lastNameRequired" }),
    ),
    birthdate: Yup.date().required(
      intl.formatMessage({ id: "privateEnroll.birthdateRequired" }),
    ),
    sex: Yup.string().required(
      intl.formatMessage({ id: "privateEnroll.sexRequired" }),
    ),
    phone: Yup.string()
      .required(intl.formatMessage({ id: "signUp.phoneRequired" }))
      .test(
        "is-valid-phone-number",
        intl.formatMessage({ id: "signUp.invalidPhoneNumber" }),
        (value) => isValidPhoneNumber(value),
      ),
    email: Yup.string()
      .email(intl.formatMessage({ id: "signUp.invalidEmail" }))
      .required(intl.formatMessage({ id: "signUp.emailRequired" })),
    postalCode: Yup.string()
      .test(
        "is-valid-postal-code",
        intl.formatMessage({ id: "attendees.invalidPostalCodeFormat" }),
        (value) => {
          if (!value) return false;
          return isValidPostalCode(value);
        },
      )
      .required(intl.formatMessage({ id: "signUp.postalCodeRequired" })),
    permisStatus: Yup.string().required(
      intl.formatMessage({ id: "attendee.permisStatus.required" }),
    ),
  });

  const handleEditAttendee = async (attendee: Attendee) => {
    let importantInfo = "";
    try {
      const response = await getAttendeeImportantInfoApi(attendee.id);
      importantInfo = response.data.importantInfo || "";
    } catch (error) {
      console.error("Failed to load important attendee information:", error);
    }
    setEditedAttendee({
      ...attendee,
      importantInfo,
      birthdate: attendee.birthdate ? new Date(attendee.birthdate) : new Date(),
      sex: attendee.sex || "other",
      postalCode: attendee.postalCode || "N/A",
    });
    setIsEditDialogOpen(true);
  };

  const handleDeleteAttendee = (attendeeId: number) => {
    Notiflix.Confirm.show(
      intl.formatMessage({ id: "user.attendee.deleteConfTitle" }),
      intl.formatMessage({ id: "user.attendee.deleteConfMessage" }),
      intl.formatMessage({ id: "common.delete" }),
      intl.formatMessage({ id: "common.cancel" }),
      () => {
        dispatch(deleteAttendee(attendeeId))
          .unwrap()
          .then(() => {
            dispatch(getProfile());
          })
          .catch((error) => {
            console.error("Failed to delete attendee:", error);
          });
      },
    );
  };

  const handleSaveEdit = (values: Partial<Attendee>) => {
    if (editedAttendee.id) {
      dispatch(
        updateAttendee({
          attendeeId: editedAttendee.id,
          attendeeData: {
            ...values,
            birthdate: values.birthdate
              ? new Date(values.birthdate)
              : new Date(),
          },
        }),
      )
        .unwrap()
        .then(() => {
          setIsEditDialogOpen(false);
          dispatch(getProfile());
        })
        .catch((error) => {
          console.error("Failed to update attendee:", error);
        });
    }
  };

  const handleChangeBirthdate = (e: any) => {
    setEditedAttendee({ ...editedAttendee, birthdate: e.target.value });
  };

  const handleOpenPayment = async (attendee: Attendee) => {
    setPaymentSelectedAttendee(attendee);
    setPaymentDialogOpen(true);

    // Fetch enrolled class IDs for this attendee
    try {
      const response = await getAttendeeEnrolledClassIdsApi(attendee.id);
      setEnrolledClassIds(response.data.enrolledClassIds || []);
    } catch (error) {
      console.error("Failed to fetch enrolled class IDs:", error);
      // If fetch fails, use empty array (will show all packages)
      setEnrolledClassIds([]);
    }
  };

  const handleClosePayment = () => {
    setPaymentDialogOpen(false);
    setPaymentSelectedAttendee(null);
    setEnrolledClassIds([]);
  };

  const handlePaymentCreated = () => {
    // Refresh attendee data to show updated remaining classes
    dispatch(getProfile());
  };

  const handleOpenPaymentHistory = async (attendee: Attendee) => {
    setSelectedAttendee(attendee);
    try {
      const response = await fetchPaymentHistoryApi(attendee.id);
      setPaymentHistory(response.data);
      setPaymentHistoryOpen(true);
    } catch (error) {
      console.error("Failed to fetch payment history:", error);
      Notiflix.Notify.failure(
        intl.formatMessage({ id: "paymentHistory.loadError" }),
      );
    }
  };

  const renderAttendeeCard = (attendee: Attendee) => (
    <Card sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <CardContent sx={{ flexGrow: 1 }}>
        <Typography variant="h6" component="div" gutterBottom>
          {`${attendee.firstName} ${attendee.lastName}`}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {`${intl.formatMessage({ id: "signUp.email" })}: ${attendee.email}`}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {`${intl.formatMessage({ id: "signUp.phone" })}: ${attendee.phone}`}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {`${intl.formatMessage({
            id: "admin.dashboard.birthdate",
          })}: ${new Date(attendee.birthdate).toLocaleDateString()}`}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {`${intl.formatMessage({ id: "admin.dashboard.sex" })}: ${
            attendee.sex === "female"
              ? intl.formatMessage({
                  id: "privateEnroll.female",
                })
              : attendee.sex === "male"
                ? intl.formatMessage({
                    id: "privateEnroll.male",
                  })
                : attendee.sex === "other"
                  ? intl.formatMessage({
                      id: "privateEnroll.other",
                    })
                  : attendee.sex
          }`}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {`${intl.formatMessage({ id: "signUp.postalCode" })}: ${
            attendee.postalCode || "N/A"
          }`}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {`${intl.formatMessage({ id: "attendee.permisStatus.title" })}: ${
            (attendee.permisStatus === "PermisS" &&
              intl.formatMessage({
                id: "attendee.permisStatus.permisS",
              })) ||
            (attendee.permisStatus === "Other" &&
              intl.formatMessage({
                id: "attendee.permisStatus.other",
              }))
          }`}
        </Typography>
        {shouldShowClassCounters(
          attendee,
          attendeeEnrolledClasses.get(attendee.id),
          classes,
        ) && (
          <Typography variant="body2" color="text.secondary">
            {`${intl.formatMessage({
              id: "attendee.remainingNonSpecialClasses",
            })}: ${attendee.remainingClasses}`}
          </Typography>
        )}
        <SpecialClassAccessDisplay attendee={attendee} />
        <Box sx={{ mt: 1 }}>
          <AttendeeImportantInfoButton attendee={attendee} />
        </Box>
      </CardContent>
      <Box sx={{ p: 2, mt: "auto" }}>
        <Button
          variant="outlined"
          size="small"
          onClick={() => handleEditAttendee(attendee)}
          sx={{ mr: 1, mb: 1 }}
        >
          <FormattedMessage id="attendees.edit" defaultMessage="Edit" />
        </Button>
        <Button
          variant="outlined"
          size="small"
          color="error"
          onClick={() => handleDeleteAttendee(attendee.id)}
          sx={{ mr: 1, mb: 1 }}
        >
          <FormattedMessage id="attendees.delete" defaultMessage="Delete" />
        </Button>
        {shouldShowClassCounters(
          attendee,
          attendeeEnrolledClasses.get(attendee.id),
          classes,
        ) && (
          <Button
            variant="outlined"
            size="small"
            color="info"
            onClick={() => handleOpenPaymentHistory(attendee)}
            sx={{ mb: 1 }}
          >
            <FormattedMessage
              id="attendees.paymentHistory"
              defaultMessage="Payment History"
            />
          </Button>
        )}
        {shouldShowClassCounters(
          attendee,
          attendeeEnrolledClasses.get(attendee.id),
          classes,
        ) && (
          <Button
            variant="contained"
            size="small"
            color="success"
            onClick={() => handleOpenPayment(attendee)}
            sx={{ mb: 1, ml: 1 }}
          >
            <FormattedMessage
              id="attendees.buyClasses"
              defaultMessage="Buy Classes"
            />
          </Button>
        )}
      </Box>
    </Card>
  );

  return (
    <Box
      sx={{
        my: 4,
        mx: 2,
        width: "100%",
        maxWidth: "1200px",
        margin: "0 auto",
        pt: isMobile ? 5 : 0,
      }}
    >
      <Button
        variant="contained"
        color="primary"
        onClick={() => setIsNewAttendeeFormOpen(true)}
        sx={{ mb: 2 }}
      >
        <FormattedMessage
          id="attendees.createNew"
          defaultMessage="Create New Attendee"
        />
      </Button>

      {attendees.length === 0 ? (
        <Typography>
          <FormattedMessage
            id="attendees.noAttendees"
            defaultMessage="No attendees found."
          />
        </Typography>
      ) : (
        <Grid container spacing={2}>
          {attendees.map((attendee) => (
            <Grid item xs={12} sm={6} key={attendee.id}>
              {renderAttendeeCard(attendee)}
            </Grid>
          ))}
        </Grid>
      )}

      <Dialog
        open={isEditDialogOpen}
        onClose={() => setIsEditDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          <FormattedMessage
            id="attendees.editAttendee"
            defaultMessage="Edit Attendee"
          />
        </DialogTitle>
        <Formik
          initialValues={editedAttendee}
          validationSchema={validationSchema}
          onSubmit={handleSaveEdit}
          enableReinitialize
        >
          {({ errors, touched, values, setFieldValue }) => (
            <Form>
              <DialogContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Field
                      as={LatinTextField}
                      name="firstName"
                      label={intl.formatMessage({
                        id: "signUp.firstName.input",
                      })}
                      error={touched.firstName && errors.firstName}
                      helperText={touched.firstName && errors.firstName}
                      fullWidth
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Field
                      as={LatinTextField}
                      name="lastName"
                      label={intl.formatMessage({
                        id: "signUp.lastName.input",
                      })}
                      error={touched.lastName && errors.lastName}
                      helperText={touched.lastName && errors.lastName}
                      fullWidth
                      required
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Field
                      as={TextField}
                      name="birthdate"
                      label={intl.formatMessage({
                        id: "admin.dashboard.birthdate",
                      })}
                      type="date"
                      InputLabelProps={{ shrink: true }}
                      error={touched.birthdate && errors.birthdate}
                      helperText={touched.birthdate && errors.birthdate}
                      fullWidth
                      required
                      inputProps={{
                        max: new Date().toISOString().split("T")[0],
                      }}
                      onChange={handleChangeBirthdate}
                      value={
                        editedAttendee.birthdate
                          ? new Date(editedAttendee.birthdate)
                              .toISOString()
                              .split("T")[0]
                          : ""
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl component="fieldset">
                      <FormLabel component="legend" required>
                        {intl.formatMessage({ id: "admin.dashboard.sex" })}
                      </FormLabel>
                      <Field as={RadioGroup} name="sex" row>
                        <FormControlLabel
                          value="female"
                          control={<Radio />}
                          label={intl.formatMessage({
                            id: "privateEnroll.female",
                          })}
                        />
                        <FormControlLabel
                          value="male"
                          control={<Radio />}
                          label={intl.formatMessage({
                            id: "privateEnroll.male",
                          })}
                        />
                        <FormControlLabel
                          value="other"
                          control={<Radio />}
                          label={intl.formatMessage({
                            id: "privateEnroll.other",
                          })}
                        />
                      </Field>
                      <ErrorMessage name="sex">
                        {(msg) => (
                          <Typography
                            color="error"
                            variant="caption"
                            component="div"
                            style={{ marginTop: 4 }}
                          >
                            {msg}
                          </Typography>
                        )}
                      </ErrorMessage>
                    </FormControl>
                  </Grid>
                  <Field
                    name="phone"
                    component={PhoneField}
                    form={{
                      errors,
                      touched,
                      setFieldValue,
                      values,
                    }}
                    label={intl.formatMessage({
                      id: "admin.dashboard.phone",
                    })}
                  />
                  <Grid item xs={12} sm={6}>
                    <Field
                      as={EmailTextField}
                      name="email"
                      label={intl.formatMessage({ id: "signUp.email" })}
                      type="email"
                      error={touched.email && errors.email}
                      helperText={touched.email && errors.email}
                      fullWidth
                      required
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Field
                      as={TextField}
                      name="postalCode"
                      label={intl.formatMessage({ id: "signUp.postalCode" })}
                      error={touched.postalCode && errors.postalCode}
                      helperText={touched.postalCode && errors.postalCode}
                      fullWidth
                      required
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Field
                      as={TextField}
                      name="importantInfo"
                      label={intl.formatMessage({
                        id: "attendee.importantInfo.label",
                      })}
                      helperText={intl.formatMessage({
                        id: "attendee.importantInfo.helper",
                      })}
                      multiline
                      minRows={3}
                      fullWidth
                      inputProps={{ maxLength: 4000 }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl
                      fullWidth
                      required
                      disabled
                      error={
                        touched.permisStatus && Boolean(errors.permisStatus)
                      }
                    >
                      <InputLabel id="permisStatus-label">
                        {intl.formatMessage({
                          id: "attendee.permisStatus",
                        })}
                      </InputLabel>
                      <Select
                        labelId="permisStatus-label"
                        id="permisStatus"
                        label={intl.formatMessage({
                          id: "attendee.permisStatus",
                        })}
                        defaultValue={values.permisStatus}
                        onChange={(e) => {
                          setFieldValue("permisStatus", e.target.value);
                        }}
                      >
                        <MenuItem value="PermisS">
                          {intl.formatMessage({
                            id: "attendee.permisStatus.permisS",
                          })}
                        </MenuItem>
                        <MenuItem value="Other">
                          {intl.formatMessage({
                            id: "attendee.permisStatus.other",
                          })}
                        </MenuItem>
                      </Select>
                      {touched.permisStatus && errors.permisStatus && (
                        <FormHelperText>{errors.permisStatus}</FormHelperText>
                      )}
                    </FormControl>
                  </Grid>
                </Grid>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setIsEditDialogOpen(false)}>
                  <FormattedMessage
                    id="common.cancel"
                    defaultMessage="Cancel"
                  />
                </Button>
                <Button type="submit" variant="contained" color="primary">
                  <FormattedMessage id="common.save" defaultMessage="Save" />
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>

      <NewAttendeeForm
        open={isNewAttendeeFormOpen}
        onClose={() => setIsNewAttendeeFormOpen(false)}
      />

      {/* Payment History Modal */}
      <Dialog
        open={paymentHistoryOpen}
        onClose={() => setPaymentHistoryOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          <FormattedMessage
            id="attendees.paymentHistory"
            defaultMessage="Payment History"
          />
          {selectedAttendee &&
            ` - ${selectedAttendee.firstName} ${selectedAttendee.lastName}`}
        </DialogTitle>
        <DialogContent>
          {paymentHistory ? (
            <Box>
              {paymentHistory.payments &&
                paymentHistory.payments.filter(
                  (payment: any) => payment.status === "SUCCESS",
                ).length > 0 && (
                  <Box mb={3}>
                    <Typography variant="h6" gutterBottom>
                      <FormattedMessage
                        id="paymentHistory.stripePayments"
                        defaultMessage="Stripe Payments"
                      />
                    </Typography>
                    {paymentHistory.payments
                      .filter((payment: any) => payment.status === "SUCCESS")
                      .map((payment: any, index: number) => (
                        <Card key={`payment-${index}`} sx={{ mb: 2, p: 2 }}>
                          <Typography variant="body1">
                            <FormattedMessage
                              id="paymentHistory.amount"
                              defaultMessage="Amount"
                            />
                            : {(payment.amount / 100).toFixed(2)} CHF
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            <FormattedMessage
                              id="paymentHistory.status"
                              defaultMessage="Status"
                            />
                            : {payment.status}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            <FormattedMessage
                              id="paymentHistory.date"
                              defaultMessage="Date"
                            />
                            : {new Date(payment.createdAt).toLocaleDateString()}
                          </Typography>
                          {payment.transactionId && (
                            <Typography variant="body2" color="text.secondary">
                              <FormattedMessage
                                id="paymentHistory.transactionId"
                                defaultMessage="Transaction ID"
                              />
                              : {payment.transactionId}
                            </Typography>
                          )}
                        </Card>
                      ))}
                  </Box>
                )}

              {paymentHistory.manualPayments &&
                paymentHistory.manualPayments.length > 0 && (
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      <FormattedMessage
                        id="paymentHistory.manualPayments"
                        defaultMessage="Manual Payments"
                      />
                    </Typography>
                    {paymentHistory.manualPayments.map(
                      (payment: any, index: number) => (
                        <Card key={`manual-${index}`} sx={{ mb: 2, p: 2 }}>
                          <Typography variant="body1">
                            <FormattedMessage
                              id="paymentHistory.amount"
                              defaultMessage="Amount"
                            />
                            :{" "}
                            {payment.price?.amount
                              ? (payment.price.amount / 100).toFixed(2)
                              : "N/A"}{" "}
                            CHF
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            <FormattedMessage
                              id="paymentHistory.classes"
                              defaultMessage="Classes"
                            />
                            : {payment.price?.numberOfClasses || "N/A"}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            <FormattedMessage
                              id="paymentHistory.date"
                              defaultMessage="Date"
                            />
                            : {new Date(payment.createdAt).toLocaleDateString()}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            <FormattedMessage
                              id="paymentHistory.type"
                              defaultMessage="Type"
                            />
                            :{" "}
                            <FormattedMessage
                              id="paymentHistory.manual"
                              defaultMessage="Manual"
                            />
                          </Typography>
                        </Card>
                      ),
                    )}
                  </Box>
                )}

              {(!paymentHistory.payments ||
                paymentHistory.payments.filter(
                  (payment: any) => payment.status === "SUCCESS",
                ).length === 0) &&
                (!paymentHistory.manualPayments ||
                  paymentHistory.manualPayments.length === 0) && (
                  <Typography>
                    <FormattedMessage
                      id="paymentHistory.noPayments"
                      defaultMessage="No payment history found."
                    />
                  </Typography>
                )}
            </Box>
          ) : (
            <Typography>
              <FormattedMessage
                id="common.loading"
                defaultMessage="Loading..."
              />
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaymentHistoryOpen(false)}>
            <FormattedMessage id="common.close" defaultMessage="Close" />
          </Button>
        </DialogActions>
      </Dialog>

      {/* Payment Dialog */}
      {paymentSelectedAttendee && (
        <PaymentDialog
          open={paymentDialogOpen}
          onClose={handleClosePayment}
          attendee={paymentSelectedAttendee}
          onPaymentCreated={handlePaymentCreated}
          classIds={enrolledClassIds}
        />
      )}
    </Box>
  );
}
